import {
  GridItemProps,
  Stack,
  Grid,
  GridItem,
  Wrap,
  WrapItem,
  Button,
  Text,
} from '@chakra-ui/react'
import { InformationMessage } from 'src/components/InformationMessage/InformationMessage'
import { MemberLabel } from 'src/components/MemberLabel/MemberLabel'
import {
  StaticScreen,
  StaticScreenTitle,
  StaticScreenContent,
  StaticScreenFooter,
} from 'src/components/StaticScreen/StaticScreen'
import { NotFound } from 'src/features/misc/components/NotFound/NotFound'
import { useMutation } from 'src/hooks/use-mutation'
import { createApiClient } from 'src/lib/api-client'
import type { components } from 'src/lib/api-client/schema.gen'
import { formatDate } from 'src/utils/formatDate'
import { getFullName } from 'src/utils/getFullName'

import { useGetReservationAbout } from '../../api/getReservationAbout'

type SignInProps = {
  trialUid: string
  trialPatientUid: string
  explanationReservationUid: string
  scene: components['schemas']['output.ReservationScene']
  onSignIn: () => void
}

export const ExplanationReservationSignIn: React.FC<SignInProps> = ({
  trialUid,
  trialPatientUid,
  explanationReservationUid,
  scene,
  onSignIn,
}) => {
  const { data: expRevAbout, error } = useGetReservationAbout({
    trialUid,
    explanationReservationUid,
  })

  const signInApi = createApiClient({
    path: '/trials/{trial_uid}/trial_patients/{trial_patient_uid}/signin_patient',
    httpMethod: 'post',
    params: {
      paths: {
        trial_uid: trialUid,
        trial_patient_uid: trialPatientUid,
      },
    },
  })

  const { request: signIn } = useMutation(signInApi.request, {
    onSuccess: onSignIn,
  })

  const headStyle: GridItemProps = {
    fontSize: 'sm',
    color: 'gray.600',
    fontWeight: 'bold',
  }

  if (!!error) {
    if (error.status === 404) {
      return <NotFound />
    }
    return null
  }

  return (
    <StaticScreen colorScheme="blue">
      <StaticScreenTitle>{expRevAbout?.trialName}</StaticScreenTitle>
      <StaticScreenContent>
        <Stack spacing="8" alignItems="center">
          <Grid
            gridTemplateColumns={{
              md: '120px minmax(0, 1fr)',
              base: '80px minmax(0, 1fr)',
              lg: '160px minmax(0, 1fr)',
            }}
            textAlign="left"
            alignItems="center"
            rowGap={4}
          >
            <GridItem {...headStyle}>医療機関</GridItem>
            <GridItem>{expRevAbout?.hospitalName}</GridItem>

            <GridItem {...headStyle}>担当者</GridItem>
            <GridItem>
              <Stack>
                {expRevAbout?.members.map(m => (
                  <MemberLabel
                    key={m.memberIndex}
                    role={m.roleName as 'Dr' | 'CRC'}
                    displayName={getFullName(m)}
                  />
                ))}
              </Stack>
            </GridItem>

            <GridItem {...headStyle}>候補ID</GridItem>
            <GridItem>
              <Wrap>
                <WrapItem>
                  <Text display="inline">{expRevAbout?.candidateID}</Text>
                </WrapItem>
                {scene === 'Explanation' && (
                  <WrapItem>
                    <Text display="inline">
                      {expRevAbout?.revisionCountName}
                    </Text>
                  </WrapItem>
                )}
              </Wrap>
            </GridItem>

            <GridItem {...headStyle}>予約日時</GridItem>
            <GridItem>
              {expRevAbout?.reservedAt &&
                formatDate(expRevAbout?.reservedAt, 'YYYY/MM/DD (ddd) HH:mm')}
            </GridItem>
          </Grid>
        </Stack>
      </StaticScreenContent>
      <StaticScreenFooter>
        {expRevAbout?.canStart ? (
          <Button
            onClick={async () => {
              await signIn()
            }}
          >
            {scene === 'Explanation' ? '説明ロビーへ' : 'ビデオ通話を開始する'}
          </Button>
        ) : (
          <InformationMessage
            message={`ビデオ通話は予約日時の15分前よりご利用いただけます。\n医療機関側で通話が開始されるか、お時間までお待ちください。`}
          />
        )}
      </StaticScreenFooter>
    </StaticScreen>
  )
}
